// Client for the REST API (spec 9). Replaces the in-memory `API` object of the prototype.
import type { AngeBotPosition, CreditRegeln, Jahresvolumen, Requirements, UserRole } from "@pipeline/core";

export interface ClientConfig {
  env: "local" | "dev" | "prod";
  version: string;
  tenantId: string;
  clientId: string;
  apiScope: string;
}

export interface Me {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  roleLabel: string;
  canWrite: boolean;
  isAdmin: boolean;
}

export interface Stage {
  id: string;
  label: string;
  closed: boolean;
}

export interface Rules {
  minBatchKg: number;
  solids: string[];
  liquids: string[];
  noGo: string[];
  packaging: string[];
  defaultFillMg: Record<string, number>;
  /** Credits per 1,000 pieces and per kg of each form, and the thresholds for 2–5 tablets. */
  volumenCredits: CreditRegeln;
}

export interface Meta {
  lastRunAt: string | null;
  lastRunStatus: "ok" | "fehler" | null;
  mailboxes: { mailbox: string; address: string; lastIngestAt: string | null; subscriptionExpiresAt: string | null }[];
  stages: Stage[];
  rules: Rules;
  angeBotPackOnly: boolean;
  /** Ange.Bot takes the batch size: product type and, if not NEM, batch size are required. */
  angeBotChargenvorgabe: boolean;
  /** aenderungsantraege: open change requests of customers (AB4); meineAufgaben: my open tasks due today or overdue; meldungen: failures at Ange.Bot I have not seen (the bell). */
  counts: { klaerfaelle: number; anfragen: number; failedJobs: number; aenderungsantraege: number; meineAufgaben: number; meldungen: number };
  /** The calendar part: switched on, and my own calendar in Pipeline.Kalender. */
  calendar: { enabled: boolean; own: boolean };
}

/** Handover between staff (admins): who left the groups with open work, and what would move. */
export interface HandoverCandidate {
  id: string;
  name: string;
  left: string[];
  deals: number;
  tasks: number;
}
export interface HandoverCounts {
  deals: number;
  tasks: number;
  meetings: number;
}

/** The AI's review of the stale tasks from the HubSpot import: the last run and what still waits. */
export interface TaskReview {
  run: {
    id: string;
    status: "queued" | "running" | "done" | "failed";
    createdAt: string;
    finishedAt: string | null;
    error: string | null;
    counts: Record<string, number> | null;
    report: { deals: { dealId: string; ref: string; name: string; company: string; changes: { task: string; due: string; outcome: "erledigt" | "ersetzen" | "offen"; reason: string; newStep: string | null }[] }[] } | null;
  } | null;
  waiting: number;
}

/** Einstellungen → Systemstatus: the calendar part and each calendar of Pipeline.Kalender. */
export interface CalendarStatus {
  enabled: boolean;
  owners: { address: string; name: string | null; status: "ok" | "no_permission" | "error" | null; lastError: string | null; statusSince: string | null; lastSyncAt: string | null; endedAt: string | null; meetings: number }[];
}

export interface VolumePoint {
  at: string;
  kgPerYear: number | null;
  eurPerYear: number | null;
  note: string | null;
  source: string;
  mail: string | null;
}

export interface Volume {
  kgPerBatch: number | null;
  batchesPerYear: number | null;
  kgPerYear: number | null;
  eurPerYear: number | null;
  units: string | null;
  basis: string | null;
  source: string;
  updatedAt: string;
  history: VolumePoint[];
}

export interface Deal {
  id: string;
  version: number;
  ref: string;
  name: string;
  companyId: string;
  company: string;
  stage: string;
  offerStage: "indikativ" | "probebatch" | "final" | null;
  ownerId: string | null;
  owner: string | null;
  product: { form?: string; pack?: string; notes?: string };
  waitingOn: "us" | "customer" | null;
  health: "ok" | "watch" | "risk" | null;
  healthReason: string | null;
  nextAction: { text: string; due: string | null; done: boolean; setBy: string | null; setAt: string | null } | null;
  summary: string | null;
  summaryAt: string | null;
  lastActivityAt: string | null;
  /** The customer's newest mail at the deal (why "Wir sind dran"). */
  lastIn: { at: string; subject: string | null; fromName: string | null } | null;
  source: string;
  needsReview: boolean;
  createdAt: string;
  closedAt: string | null;
  closedReason: string | null;
  /** `hidden`: no longer with the company; still listed at the deal, never offered again. */
  contacts: { id: string; name: string | null; email: string | null; role: string | null; hidden: boolean }[];
  volume: Volume | null;
  /** The AI's estimate per year and form, written with the situation; null before the first one. */
  jahresvolumen: Jahresvolumen | null;
  /** From the positions and the situation: product types, finished goods or bulk, the AI's product type, and Pharma (medicine or medical device). */
  profil: { produktarten: string[]; lieferform: "fertigware" | "bulk" | "gemischt" | null; regulatorik: string | null; pharma: boolean };
}

/** A contact the deal's pickers offer: the company's, not hidden. */
export interface ContactOption {
  id: string;
  name: string | null;
  email: string | null;
  role: string | null;
}

export interface Location {
  mailbox: string;
  folder: string | null;
  webLink: string | null;
}

export interface Attachment {
  id: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  documentType: string | null;
  summary: string | null;
}

/** What the pipeline may do in the mailboxes (setting mail_actions, spec 8). */
export interface MailActions {
  send: "off" | "test" | "live";
  drafts: boolean;
  infoMove: boolean;
  /** The pipeline reads my mailbox (Pipeline.Postfach): I may answer with my own address. */
  ownMailbox: boolean;
  /** In Pipeline.Senden without a mailbox read: my own address too, sent in one call, no draft in Outlook. */
  sendOnly: boolean;
}

export interface Outgoing {
  id: string;
  kind: "send" | "draft" | "direct";
  status: "queued" | "sent" | "draft" | "failed";
  test: boolean;
  webLink: string | null;
  error: string | null;
}

/** The hand-over to Ange.Bot of a deal (docs/schnittstelle-ange-bot.md). */
export interface AngeBotState {
  enabled: boolean;
  ready: boolean;
  /** Values Ange.Bot cannot take (or no position): no push; `fehlend` goes only with a written reason (03.11.2026). */
  gesperrt: string[];
  fehlend: string[];
  /** A push despite missing essentials is possible (once Ange.Bot takes it). */
  mitGrund: boolean;
  missing: { position: string; fields: string[] }[];
  flags: string[];
  anfrageNr: string | null;
  pushes: {
    id: string;
    art: "indikativ" | "final";
    status: "queued" | "accepted" | "rejected" | "failed";
    anfrageNr: string | null;
    maengel: { position: number | null; feld: string | null; text: string }[];
    hinweise: { art: string; produktname: string | null; position: number | null; feld: string | null; text: string }[];
    error: string | null;
    createdAt: string;
    /** Pushed despite missing essentials: the written reason and what was missing. */
    grund: string | null;
    fehlend: string[];
    by: string | null;
  }[];
  events: { id: string; typ: "stand" | "rueckfrage" | "angebot"; anfrageNr: string | null; zeitpunkt: string; daten: Record<string, unknown> }[];
  /** Ange.Bot's feasibility check (docs 3.5). */
  machbarkeit: {
    enabled: boolean;
    pending: boolean;
    last: {
      status: "queued" | "done" | "failed";
      trigger: "knopf" | "auto";
      error: string | null;
      maengel: { position: number | null; feld: string | null; text: string }[];
      createdAt: string;
      by: string | null;
    } | null;
    /** index: the deal's position; aktuell: the position is still as checked. */
    positions: MachbarkeitVerdict[];
  };
}

export interface Inquiry {
  id: string;
  version: number;
  status: "neu" | "deal" | "rueckfrage" | "absage" | "ignoriert";
  grade: "A" | "B" | "C";
  gradedBy: "ai" | "manual";
  gradeReason: string | null;
  receivedAt: string;
  subject: string | null;
  excerpt: string | null;
  mailId: string | null;
  mailCount: number;
  hasOriginal: boolean;
  fromName: string | null;
  fromAddr: string;
  freemail: boolean | null;
  company: string | null;
  companyId: string | null;
  existingCompany: boolean;
  website: string | null;
  source: string;
  facts: {
    product?: string | null;
    form?: string | null;
    pack?: string | null;
    quantity?: string | null;
    mass_kg?: number | null;
    mass_calc?: string | null;
    kg_per_year?: number | null;
    vol_source?: string | null;
  };
  checks: { key: string; label: string; ok: boolean | null; note: string }[];
  drafts: { rueckfrage: string | null; absage: string | null };
  dealId: string | null;
  /** Open tasks of the inquiry. */
  tasks: Pick<Task, "id" | "version" | "text" | "due" | "assignee" | "source">[];
  /** Product type and annual volume read from the inquiry's mails (03.11.2026); null before. */
  regulatorik: string | null;
  pharma: boolean;
  jahresvolumen: Jahresvolumen | null;
}

export interface CompanySummary {
  id: string;
  name: string;
  domains: string[];
  city: string | null;
  country: string | null;
  kind: string | null;
  openDeals: number;
  deals: number;
  lastActivityAt: string | null;
}

export interface Company {
  id: string;
  version: number;
  name: string;
  domains: string[];
  website: string | null;
  addressExtra: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  vatId: string | null;
  commercialRegister: string | null;
  phone: string | null;
  email: string | null;
  industry: string | null;
  kind: string | null;
  description: string | null;
  employees: number | null;
  linkedin: string | null;
  notes: string | null;
  fromHubSpot: boolean;
  /** Given by auftrag when the company becomes a customer (AB6); read only. */
  customerNumber: string | null;
}

export interface Contact {
  id: string;
  version: number;
  /** With first or last name given it follows from title, first and last name. */
  name: string | null;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
  phone: string | null;
  mobile: string | null;
  salutation: string | null;
  letterSalutation: string | null;
  linkedin: string | null;
  city: string | null;
  country: string | null;
  language: string | null;
  extra: Record<string, string>;
  /** „Nicht mehr bei der Firma“: hidden from the lists and pickers, never deleted. */
  hiddenAt: string | null;
  hiddenBy: string | null;
}

export interface ChronicleItem {
  id: string;
  /** ereignis: written by auftrag, a milestone with its link or a change to the address book. */
  kind: "mail" | "note" | "call" | "meeting" | "task" | "ereignis";
  at: string;
  title: string | null;
  body: string | null;
  direction: "in" | "out" | null;
  /** Call: minutes; meeting: end, place, participants, called off. */
  durationMin: number | null;
  endsAt: string | null;
  location: string | null;
  attendees: Attendee[];
  cancelled: boolean;
  from: string | null;
  author: string | null;
  dealId: string | null;
  dealRef: string | null;
  hasOriginal: boolean;
  attachments: Attachment[];
  link: string | null;
  person: { id: string; name: string | null } | null;
}

export interface Attendee {
  name: string | null;
  email: string | null;
  contactId: string | null;
}

/** An open task (decision 01.11.2026) with where it belongs; the deal's earliest one is its next step. */
export interface Task {
  id: string;
  version: number;
  text: string;
  body: string | null;
  due: string | null;
  assignee: { id: string; name: string } | null;
  source: "manual" | "ai" | "ange_bot" | "hubspot";
  author: string | null;
  createdAt: string;
  deal: { id: string; ref: string; name: string; closed: boolean } | null;
  company: { id: string; name: string } | null;
  inquiry: { id: string; label: string } | null;
}

/** Where a task, note, call or meeting is recorded: exactly one. */
export type ActivityTarget = { dealId: string } | { companyId: string } | { inquiryId: string };
export type NewActivity = ActivityTarget &
  (
    | { type: "task"; text: string; due?: string | null; assigneeId?: string | null }
    | { type: "note"; body: string; at?: string }
    | { type: "call"; direction: "in" | "out"; at?: string; durationMin?: number | null; subject?: string | null; body?: string | null }
    | { type: "meeting"; subject: string; at: string; endsAt?: string | null; location?: string | null; body?: string | null; attendees?: { contactId?: string; name?: string; email?: string }[] }
  );

/** Further addresses and the mails for invoices and orders (address book, M10). */
export interface CompanyAddress {
  id: string;
  version: number;
  kind: "rechnung" | "lieferung" | "weitere";
  label: string | null;
  extra: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
}
export interface CompanyEmail {
  id: string;
  version: number;
  purpose: "rechnung" | "bestellung";
  email: string;
  label: string | null;
}
export type CompanyList = "addresses" | "emails";

export interface CompanyDetail {
  company: Company;
  /** A merged duplicate (AB3): the company it went into. */
  mergedInto: { id: string; name: string } | null;
  /** Open change requests of the customer from the auftrag portal (AB4). */
  changeRequests: ChangeRequest[];
  addresses: CompanyAddress[];
  emails: CompanyEmail[];
  contacts: Contact[];
  deals: { id: string; ref: string; name: string; stage: string; offerStage: string | null; closed: boolean; lastActivityAt: string | null }[];
  chronicle: ChronicleItem[];
  /** Open tasks of the company and its deals. */
  tasks: Task[];
}

/** A change request from the auftrag portal (AB4): per field the value now and the requested one. */
export interface ChangeRequest {
  id: string;
  version: number;
  target: "firma" | "person" | "adresse";
  /** A new person or address. */
  creates: boolean;
  company: { id: string; name: string };
  /** The record as it is now; null for a new one or one deleted meanwhile. */
  record: { id: string; version: number; label: string } | null;
  submittedBy: { name: string | null; email: string };
  submittedAt: string;
  comment: string | null;
  fields: { field: string; label: string; current: string | null; atSubmission: string | null; requested: string | null }[];
}
export type ChangeDecision = "angenommen" | "abgelehnt";

/** Merging duplicates (AB3, admins only). */
export interface MergeBody {
  into: string;
  version: number;
  intoVersion: number;
}
export interface MergePreview {
  source: { id: string; name: string; version: number; domains: string[] };
  target: { id: string; name: string; version: number; domains: string[] };
  moves: { deals: number; contacts: number; mails: number; inquiries: number; activities: number; addresses: number; emails: number; emailsDuplicate: number };
}
export interface DuplicateGroup {
  reasons: { by: "domain" | "name" | "ust_idnr"; key: string }[];
  /** Most deals first. */
  companies: { id: string; name: string; domains: string[]; city: string | null; vatId: string | null; deals: number; openDeals: number }[];
}
/** How the messages "alt → neu" reach a subscribed app (Systemstatus). */
export interface AddressBookConsumerStatus {
  consumer: "auftrag" | "angebot";
  reachable: boolean;
  url: string | null;
  open: number;
  failing: number;
  failingSince: string | null;
  lastError: string | null;
  lastDeliveredAt: string | null;
  rejected: number;
  lastRejection: string | null;
  conflicts: number;
}

/** Note, call, meeting or done task, from the HubSpot migration (spec 12) or recorded in the pipeline. */
export interface Activity {
  id: string;
  type: "note" | "call" | "meeting" | "task";
  subject: string | null;
  body: string | null;
  at: string;
  author: string | null;
  source: string;
  direction: "in" | "out" | null;
  durationMin: number | null;
  endsAt: string | null;
  location: string | null;
  attendees: Attendee[];
  doneAt: string | null;
  /** Closed by the AI's review of the stale imported tasks, with its reason. */
  doneByAi: boolean;
  doneNote: string | null;
  cancelledAt: string | null;
  attachments: Attachment[];
  /** Outlook meetings: our people in it, the calendars holding it (with that calendar's link), the Teams link. */
  internalAttendees: { name: string | null; email: string }[];
  calendars: { owner: string; name: string | null; webLink: string | null }[];
  onlineUrl: string | null;
  calendarState: "pending" | "created" | "failed" | null;
  calendarError: string | null;
}

export interface DealMail {
  forwardedBy: string | null;
  id: string;
  version: number;
  direction: "in" | "out";
  fromAddr: string;
  fromName: string | null;
  toAddrs: string[];
  subject: string | null;
  receivedAt: string;
  summary: string | null;
  reason: string | null;
  confidence: number | null;
  assignedBy: string | null;
  feedback: "confirmed" | "corrected" | null;
  needsReview: boolean;
  hasOriginal: boolean;
  locations: Location[];
  attachments: Attachment[];
}

export interface Mail {
  id: string;
  version: number;
  category: "deal" | "anfrage" | "firma" | "unklar";
  direction: "in" | "out";
  fromAddr: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
  summary: string | null;
  reason: string | null;
  confidence: number | null;
  assignedBy: string | null;
  dealId: string | null;
  dealRef: string | null;
  companyId: string | null;
  companyName: string | null;
  candidates: { dealId: string; ref: string; name: string; company: string; score: number; reason: string }[];
  feedback: "confirmed" | "corrected" | null;
  locations: Location[];
}

export type MailDecision =
  | { action: "confirm"; note?: string }
  | { action: "assign"; dealId: string; note?: string }
  | { action: "firma"; companyId: string; note?: string }
  | { action: "anfrage"; note?: string }
  | { action: "unklar"; note?: string }
  | { action: "kein_bezug"; note?: string };

export interface Original {
  subject: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  date: string | null;
  html: string;
}

export interface Settings {
  rules: Rules;
  briefingRecipients: string[];
  assignThreshold: number;
}

export interface Exclusion {
  value: string;
  kind: "address" | "domain";
  note: string | null;
}

export interface Quality {
  days: number;
  automatic: number;
  corrected: number;
  hitRate: number | null;
  klaerfaelle: number;
}

/** What "Push nach Ange.Bot" would send now, as the preview before the click shows it (the fields shown there). */
export interface PushBody {
  ref: string;
  art: "indikativ" | "final";
  gesendet_von: { name: string | null; email: string | null };
  firma: { name: string; domain: string | null; strasse: string | null; plz: string | null; ort: string | null; land: string | null };
  ansprechpartner: { name: string | null; email: string | null; telefon: string | null };
  positionen: AngeBotPosition[];
  anhaenge: { id: string; dateiname: string; groesse: number | null }[];
}

/** An open question of Ange.Bot's check and who answers it. */
export interface Frage {
  text: string;
  an: "kunde" | "produktion";
}

/**
 * Ange.Bot's verdict on a position of the deal. From machbarkeit/3 on (chat-530, chat-538), else null: the short
 * sentence, the questions that can tip the verdict, those to clarify before the offer, batches as a statement.
 */
export interface MachbarkeitVerdict {
  index: number;
  urteil: "machbar" | "unklar" | "nicht_machbar";
  begruendung: string;
  anlagen: string[];
  offen: string[];
  kurzsatz: string | null;
  fragen: Frage[] | null;
  vorAngebot: Frage[] | null;
  chargen: string | null;
  geprueftAm: string;
  aktuell: boolean;
}

/** A push to Ange.Bot or a feasibility check, as the log ("Protokoll") lists it. */
export interface LogEntry {
  kind: "push" | "machbarkeit";
  id: string;
  status: string;
  /** Push: indikativ or final; check: knopf or auto. */
  how: string | null;
  dealId: string;
  ref: string;
  deal: string;
  at: string;
  doneAt: string | null;
  by: string | null;
  /** What came of it beyond the status, in one line (number, findings, verdicts, the reason of a failure); may be empty. */
  outcome: string;
  /** A push despite missing essentials: the written reason. */
  grund: string | null;
}

/** A failed push or check in the bell (Meldungen). */
export interface Alert extends LogEntry {
  unread: boolean;
}

export interface Briefing {
  date: string;
  headline: string | null;
  summary: string | null;
  stats: Record<string, unknown> | null;
  items: { prio?: string; title?: string; text?: string; dealId?: string; view?: string; due?: string }[] | null;
  generatedAt: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The rest of the error body, e.g. the contact that holds an address (email_taken). */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function parseError(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as ({ error?: { code?: string; message?: string } } & Record<string, unknown>) | null;
  const { error, ...details } = body ?? {};
  return new ApiError(res.status, error?.code ?? "http_error", error?.message ?? `HTTP ${res.status}`, details);
}

export async function loadClientConfig(): Promise<ClientConfig> {
  const res = await fetch("/api/client-config", { headers: { accept: "application/json" } });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

interface RequestOptions {
  body?: unknown;
  /** Current row version for optimistic locking (sent as If-Match; 409 on conflict). */
  version?: number;
}

/** Global search (GET /api/search); snippets mark hits with \u0002…\u0003. */
export interface SearchResult {
  q: string;
  deals: { id: string; ref: string; name: string; company: string; stage: string; closed: boolean }[];
  companies: { id: string; name: string; domain: string | null; city: string | null; openDeals: number }[];
  contacts: { id: string; name: string | null; email: string | null; companyId: string | null; company: string | null }[];
  inquiries: { id: string; name: string | null; email: string; company: string | null; grade: string; status: string; dealId: string | null }[];
  mails: {
    id: string;
    subject: string | null;
    from: string;
    receivedAt: string;
    category: string;
    snippet: string;
    dealId: string | null;
    dealRef: string | null;
    inquiryId: string | null;
    companyId: string | null;
    hasOriginal: boolean;
  }[];
  attachments: { id: string; filename: string; snippet: string; mailId: string | null; dealId: string | null; dealRef: string | null }[];
  notes: { id: string; type: string; at: string; snippet: string; dealId: string | null; dealRef: string | null; companyId: string | null }[];
}

/** The global Claude (decision 31.10.2026). */
export interface ClaudeChatSummary {
  id: string;
  title: string;
  updatedAt: string;
  running: boolean;
}
export interface ClaudeDocSummary {
  id: string;
  title: string;
  dealId: string | null;
  dealRef: string | null;
  createdAt: string;
  by: string;
}
export interface ClaudeChat {
  id: string;
  title: string;
  messages: { id: string; role: "user" | "assistant"; text: string; web: boolean; status: "running" | "done" | "error"; steps: { label: string }[]; error: string | null; createdAt: string }[];
  documents: ClaudeDocSummary[];
}
export interface ClaudeDoc extends ClaudeDocSummary {
  markdown: string;
  chatId: string | null;
  mine: boolean;
}

export function createApi(getToken: () => Promise<string>) {
  async function send(method: string, path: string, opts: RequestOptions = {}): Promise<Response> {
    const headers: Record<string, string> = { accept: "application/json", authorization: `Bearer ${await getToken()}` };
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    if (opts.version !== undefined) headers["if-match"] = String(opts.version);
    const res = await fetch(`/api${path}`, { method, headers, body: opts.body === undefined ? undefined : JSON.stringify(opts.body) });
    if (!res.ok) throw await parseError(res);
    return res;
  }
  async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await send(method, path, opts);
    return (res.status === 204 ? undefined : await res.json()) as T;
  }
  const qs = (p: Record<string, string | undefined>) => {
    const s = new URLSearchParams(Object.entries(p).filter((e): e is [string, string] => e[1] !== undefined)).toString();
    return s ? `?${s}` : "";
  };

  return {
    me: () => request<Me>("GET", "/me"),
    meta: () => request<Meta>("GET", "/meta"),
    briefing: async () => (await request<{ briefing: Briefing | null }>("GET", "/briefings/latest")).briefing,
    users: async () => (await request<{ items: { id: string; name: string; role: string }[] }>("GET", "/users")).items,
    deals: async (closed: boolean) => (await request<{ items: Deal[] }>("GET", `/deals${qs({ closed: String(closed) })}`)).items,
    deal: (id: string) =>
      request<Deal & { activities: Activity[]; tasks: Task[]; requirements: Requirements | null; requirementsAt: string | null; contactOptions: ContactOption[] }>("GET", `/deals/${id}`),
    tasks: async (scope: "mine" | "all") => (await request<{ items: Task[] }>("GET", `/tasks${qs({ scope })}`)).items,
    createActivity: (body: NewActivity) => request<{ id: string; version: number }>("POST", "/activities", { body }),
    createCalendarEvent: (dealId: string, body: { subject: string; at: string; endsAt: string; location?: string | null; contactIds: string[]; teams: boolean }) =>
      request<{ id: string; version: number }>("POST", `/deals/${dealId}/calendar-events`, { body }),
    calendarStatus: () => request<CalendarStatus>("GET", "/calendar/status"),
    taskReview: () => request<TaskReview>("GET", "/tasks/review-imported"),
    startTaskReview: (force: boolean) => request<{ runId: string }>("POST", "/tasks/review-imported", { body: { force } }),
    handoverCandidates: async () => (await request<{ items: HandoverCandidate[] }>("GET", "/handover/candidates")).items,
    handoverPreview: (from: string, to: string) => request<HandoverCounts & { from: string; to: string }>("GET", `/handover/preview${qs({ from, to })}`),
    handover: (from: string, to: string, expected: HandoverCounts) => request<HandoverCounts>("POST", "/handover", { body: { from, to, expected } }),
    updateTask: (id: string, version: number, body: { text?: string; due?: string | null; assigneeId?: string | null; done?: boolean }) =>
      request<{ id: string; version: number }>("PATCH", `/activities/${id}`, { body, version }),
    addDealContact: (id: string, contactId: string) => request<Deal>("POST", `/deals/${id}/contacts`, { body: { contactId } }),
    /** Frag Claude (spec 7.6): calls onText per streamed piece; throws with the server's message on an error event. */
    ask: async (id: string, question: string, onText: (text: string) => void): Promise<void> => {
      const res = await send("POST", `/deals/${id}/ask`, { body: { question } });
      const reader = res.body!.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        buffer += value;
        let cut: number;
        while ((cut = buffer.indexOf("\n\n")) >= 0) {
          const block = buffer.slice(0, cut);
          buffer = buffer.slice(cut + 2);
          const event = /^event: (.+)$/m.exec(block)?.[1] ?? "message";
          const data = JSON.parse(/^data: (.*)$/m.exec(block)?.[1] ?? "{}") as { text?: string; message?: string };
          if (event === "error") throw new Error(data.message ?? "Claude hat nicht geantwortet.");
          if (event === "done") return;
          if (data.text) onText(data.text);
        }
      }
    },
    correctVolume: (id: string, body: { kgPerBatch: number | null; batchesPerYear: number | null; kgPerYear: number | null; eurPerYear: number | null; units: string | null; note: string }) =>
      request<Deal>("POST", `/deals/${id}/volume`, { body }),
    refreshRequirements: (id: string) => request<{ queued: boolean }>("POST", `/deals/${id}/requirements/refresh`),
    saveRequirements: (id: string, version: number, body: Requirements) => request<{ version: number }>("PUT", `/deals/${id}/requirements`, { body, version }),
    createDeal: (body: {
      name: string;
      companyId?: string;
      companyName?: string;
      contacts?: { name?: string; email: string }[];
      nextAction?: { text: string; due?: string | null };
    }) => request<Deal>("POST", "/deals", { body }),
    updateDeal: (id: string, version: number, body: Record<string, unknown>) => request<Deal>("PATCH", `/deals/${id}`, { body, version }),
    inquiries: async (done: boolean) => (await request<{ items: Inquiry[] }>("GET", `/inquiries${qs({ done: done ? "true" : undefined })}`)).items,
    updateInquiry: (id: string, version: number, body: Record<string, unknown>) =>
      request<{ inquiry: Inquiry | null; deleted: boolean }>("PATCH", `/inquiries/${id}`, { body, version }),
    convertInquiry: (id: string, body: { name?: string } = {}) => request<{ dealId: string; ref: string }>("POST", `/inquiries/${id}/convert`, { body }),
    releaseInquiry: (id: string, version: number, body: { kind: "rueckfrage" | "absage"; body: string }) =>
      request<{ outgoingId: string }>("POST", `/inquiries/${id}/release`, { body, version }),
    releaseInquiries: async (items: { id: string; version: number; kind: "rueckfrage" | "absage"; body: string }[]) =>
      (await request<{ results: { id: string; outgoingId: string | null; error: string | null }[] }>("POST", "/inquiries/release", { body: { items } })).results,
    mailActions: () => request<MailActions>("GET", "/mail-actions"),
    replyDraft: async (dealId: string, mailId: string) => (await request<{ body: string }>("POST", `/deals/${dealId}/reply-draft`, { body: { mailId } })).body,
    reply: (dealId: string, body: { mailId: string; body: string; via: "own" | "outlook" | "sales"; attachmentIds: string[]; requestId?: string }) =>
      request<{ outgoingId: string }>("POST", `/deals/${dealId}/replies`, { body }),
    upload: async (dealId: string, file: File) => {
      const res = await fetch(`/api/deals/${dealId}/uploads`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${await getToken()}`,
          accept: "application/json",
          "content-type": "application/octet-stream",
          "x-file-name": encodeURIComponent(file.name),
          "x-file-type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!res.ok) throw await parseError(res);
      return (await res.json()) as { id: string; filename: string; contentType: string; sizeBytes: number };
    },
    outgoing: (id: string) => request<Outgoing>("GET", `/outgoing/${id}`),
    angeBot: (dealId: string) => request<AngeBotState>("GET", `/deals/${dealId}/ange-bot`),
    pushPreview: async (dealId: string, art: "indikativ" | "final") => (await request<{ body: PushBody }>("GET", `/deals/${dealId}/ange-bot/preview${qs({ art })}`)).body,
    pushAngeBot: (dealId: string, art: "indikativ" | "final", grund?: string) => request<{ pushId: string }>("POST", `/deals/${dealId}/ange-bot/push`, { body: { art, ...(grund && { grund }) } }),
    checkMachbarkeit: (dealId: string) => request<{ checkId: string }>("POST", `/deals/${dealId}/ange-bot/machbarkeit`),
    alerts: async () => (await request<{ items: Alert[] }>("GET", "/alerts")).items,
    alertsSeen: () => request<void>("POST", "/alerts/seen"),
    angeBotLog: async (before?: string) => (await request<{ items: LogEntry[] }>("GET", `/ange-bot/log${qs({ before })}`)).items,
    search: (q: string) => request<SearchResult>("GET", `/search${qs({ q })}`),
    claudeChats: async () => (await request<{ items: ClaudeChatSummary[] }>("GET", "/claude/chats")).items,
    claudeChat: (id: string) => request<ClaudeChat>("GET", `/claude/chats/${id}`),
    claudeStart: async (text: string, web: boolean) => (await request<{ id: string }>("POST", "/claude/chats", { body: { text, web } })).id,
    claudeAsk: (id: string, text: string, web: boolean) => request<{ ok: true }>("POST", `/claude/chats/${id}/messages`, { body: { text, web } }),
    claudeDoc: (id: string) => request<ClaudeDoc>("GET", `/claude/documents/${id}`),
    claudeDocDeal: (id: string, ref: string | null) => request<ClaudeDocSummary>("PUT", `/claude/documents/${id}/deal`, { body: { ref } }),
    dealDocuments: async (id: string) => (await request<{ items: ClaudeDocSummary[] }>("GET", `/deals/${id}/documents`)).items,
    companies: async (q?: string, all?: boolean) => (await request<{ items: CompanySummary[] }>("GET", `/companies${qs({ q: q || undefined, all: all ? "true" : undefined })}`)).items,
    company: (id: string) => request<CompanyDetail>("GET", `/companies/${id}`),
    updateCompany: (id: string, version: number, body: Record<string, unknown>) => request<Company>("PATCH", `/companies/${id}`, { body, version }),
    updateContact: (id: string, version: number, body: Record<string, unknown>) => request<Contact>("PATCH", `/contacts/${id}`, { body, version }),
    addContact: (companyId: string, body: Record<string, unknown>) => request<Contact>("POST", `/companies/${companyId}/contacts`, { body }),
    addCompanyItem: (companyId: string, list: CompanyList, body: Record<string, unknown>) => request<unknown>("POST", `/companies/${companyId}/${list}`, { body }),
    updateCompanyItem: (companyId: string, list: CompanyList, id: string, version: number, body: Record<string, unknown>) =>
      request<unknown>("PATCH", `/companies/${companyId}/${list}/${id}`, { body, version }),
    deleteCompanyItem: (companyId: string, list: CompanyList, id: string, version: number) => request<void>("DELETE", `/companies/${companyId}/${list}/${id}`, { version }),
    companyDuplicates: async () => (await request<{ items: DuplicateGroup[] }>("GET", "/companies/duplicates")).items,
    mergePreview: (id: string, into: string) => request<MergePreview>("GET", `/companies/${id}/merge-preview${qs({ into })}`),
    mergeCompany: (id: string, body: MergeBody) => request<{ id: string }>("POST", `/companies/${id}/merge`, { body }),
    mergeContact: (id: string, body: MergeBody) => request<{ id: string }>("POST", `/contacts/${id}/merge`, { body }),
    mergeAddress: (companyId: string, id: string, body: MergeBody) => request<{ id: string }>("POST", `/companies/${companyId}/addresses/${id}/merge`, { body }),
    addressBookConsumers: async () => (await request<{ items: AddressBookConsumerStatus[] }>("GET", "/address-book/consumers")).items,
    changeRequests: async (companyId?: string) => (await request<{ items: ChangeRequest[] }>("GET", `/change-requests${qs({ companyId })}`)).items,
    decideChangeRequest: (id: string, body: { version: number; recordVersion: number | null; ergebnis: Record<string, ChangeDecision>; kommentar: string | null }) =>
      request<{ id: string; status: "entschieden"; newId: string | null }>("POST", `/change-requests/${id}/decision`, { body }),
    dealMails: async (id: string) => (await request<{ items: DealMail[] }>("GET", `/deals/${id}/mails`)).items,
    mails: async (category: Mail["category"]) => (await request<{ items: Mail[] }>("GET", `/mails${qs({ category })}`)).items,
    decideMail: (id: string, version: number, body: MailDecision) => request<{ mail: Mail | null; deleted: boolean }>("PATCH", `/mails/${id}`, { body, version }),
    original: (id: string) => request<Original>("GET", `/mails/${id}/original`),
    attachment: async (id: string) => (await send("GET", `/attachments/${id}`)).blob(),
    quality: () => request<Quality>("GET", "/quality"),
    settings: () => request<Settings>("GET", "/settings"),
    saveSettings: (body: Settings) => request<Settings>("PUT", "/settings", { body }),
    exclusions: async () => (await request<{ items: Exclusion[] }>("GET", "/exclusions")).items,
    addExclusion: (body: { value: string; kind: "address" | "domain"; note?: string }) => request<Exclusion>("POST", "/exclusions", { body }),
    deleteExclusion: (value: string) => request<void>("DELETE", `/exclusions/${encodeURIComponent(value)}`),
  };
}

export type Api = ReturnType<typeof createApi>;
